const API_BASE = process.env.REACT_APP_API_URL;

async function callBackend(question, steps, provider, token, timeElapsed) {
  const res = await fetch(`${API_BASE}/api/mark-answer`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ question, steps, provider, timeElapsed }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Server error: ${res.statusText}`);
  }

  return res.json();
}

/**
 * Marks an outline_4 answer.
 * @param {Object} question    - Question object from questions.js
 * @param {Object} steps       - { 'POINT 1': '', 'DEVELOPMENT 1': '', 'POINT 2': '', 'DEVELOPMENT 2': '' }
 * @param {string} provider    - 'gemini' | 'claude'
 * @param {string} token       - Auth0 access token
 * @param {number} timeElapsed - Seconds spent on the question
 */
export async function markOutline4(question, steps, provider = 'gemini', token, timeElapsed = 0) {
  return callBackend(question, steps, provider, token, timeElapsed);
}

/**
 * Marks an assess_6 answer.
 * @param {Object} question    - Question object
 * @param {Object} steps       - { 'CONTEXT (AO1)': '', 'ASSESSED POINT 1 (AO2)': '', ... }
 * @param {string} provider    - 'gemini' | 'claude'
 * @param {string} token       - Auth0 access token
 * @param {number} timeElapsed - Seconds spent on the question
 */
export async function markAssess6(question, steps, provider = 'gemini', token, timeElapsed = 0) {
  return callBackend(question, steps, provider, token, timeElapsed);
}

/**
 * Marks a scaffold_9 answer (9-mark AO1/AO2 structured scaffold).
 * Returns score + justification only. Call markScaffold9Breakdown for paragraph feedback.
 * @param {Object} question    - Question object with archetype 'scaffold_9'
 * @param {Object} steps       - 9-card steps keyed by card label
 * @param {string} provider    - 'gemini' | 'claude'
 * @param {string} token       - Auth0 access token
 * @param {number} timeElapsed - Seconds spent on the question
 */
export async function markScaffold9(question, steps, provider = 'claude', token, timeElapsed = 0) {
  return callBackend(question, steps, provider, token, timeElapsed);
}

/**
 * Fetches per-paragraph AO breakdown for a scaffold_9 answer (second LLM call).
 * @param {string} attemptId   - Attempt ID returned by markScaffold9
 * @param {Object} question    - Question object
 * @param {Object} steps       - 9-card steps keyed by card label
 * @param {string} provider    - 'gemini' | 'claude'
 * @param {Object} firstResult - { awarded_mark, awarded_level } from markScaffold9
 * @param {string} token       - Auth0 access token
 */
export async function markScaffold9Breakdown(attemptId, question, steps, provider = 'claude', firstResult, token) {
  const res = await fetch(`${API_BASE}/api/scaffold9-breakdown`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ attemptId, question, steps, provider, firstResult }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Server error: ${res.statusText}`);
  }

  return res.json();
}

/**
 * Marks an essay_20 answer (20-mark AQA essay).
 * Returns score + justification only. Call markEssay20Breakdown for paragraph feedback.
 * @param {Object} question    - Question object with archetype 'essay_20'
 * @param {Object} steps       - 6-card steps keyed by card label
 * @param {string} provider    - 'gemini' | 'claude'
 * @param {string} token       - Auth0 access token
 * @param {number} timeElapsed - Seconds spent on the question
 */
export async function markEssay20(question, steps, provider = 'claude', token, timeElapsed = 0) {
  return callBackend(question, steps, provider, token, timeElapsed);
}

/**
 * Fetches per-paragraph AO breakdown for an essay_20 answer (second LLM call).
 * @param {string} attemptId   - Attempt ID returned by markEssay20
 * @param {Object} question    - Question object
 * @param {Object} steps       - 6-card steps keyed by card label
 * @param {string} provider    - 'gemini' | 'claude'
 * @param {Object} firstResult - { awarded_mark, awarded_level } from markEssay20
 * @param {string} token       - Auth0 access token
 */
export async function markEssay20Breakdown(attemptId, question, steps, provider = 'claude', firstResult, token) {
  const res = await fetch(`${API_BASE}/api/essay20-breakdown`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ attemptId, question, steps, provider, firstResult }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Server error: ${res.statusText}`);
  }

  return res.json();
}
